import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Container,
  Autocomplete,
  Snackbar,
  Alert,
  AlertTitle,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAgencyBookingReportRule,
  getActiveCustomers,
  getCustomerByID,
  getAgencyBookingReportRuleByID,
  updateAgencyBookingReportRule,
} from "../redux/actions/AdminAction";
import { useNavigate, useParams } from "react-router-dom";
import { GET_AGENCY_BOOKING_REPORT_RULE_RESET } from "../Constants/Constants";

const DEFAULT_SUCCESS_QUEUE = 16;
const DEFAULT_RUN_TIME = "0200";
const DEFAULT_EMAIL_ENABLED = true;
const DEFAULT_PICK_FROM_CATEGORY = false;
const DEFAULT_TICKETED_STATUS = null;
const DEFAULT_AIRLINES = "";

const getCustomerOptionsFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.customers)) return payload.customers;
  if (Array.isArray(payload?.results)) return payload.results;
  if (payload && typeof payload === "object") return Object.values(payload);
  return [];
};

export default function AgencyBookingReportRule() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [rule_name, setRule_name] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [customerId, setCustomerId] = useState();
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [PCCs, setPCCs] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);

  const GetAgencyBookingReportRuleByID = useSelector(
    (state) => state.GetAgencyBookingReportRuleByID
  );
  const { ruleInfo } = GetAgencyBookingReportRuleByID;

  const GetAllActiveCustomers = useSelector(
    (state) => state.GetAllActiveCustomers
  );
  const { activeCustomersInfo, loading: customerOptionsLoading } =
    GetAllActiveCustomers;

  const GetCustomerByID = useSelector((state) => state.GetCustomerByID);
  const { customerById } = GetCustomerByID;

  const AgencyBookingReportCreateRule = useSelector(
    (state) => state.AgencyBookingReportCreateRule
  );
  const { agencyBookingReportRuleInfo, error } = AgencyBookingReportCreateRule;

  const UpdateAgencyBookingReportRule = useSelector(
    (state) => state.UpdateAgencyBookingReportRule
  );
  const { success, message, updateerror } = UpdateAgencyBookingReportRule;

  const token = JSON.parse(localStorage.getItem("userInfo"));
  const customerOptions = useMemo(
    () => getCustomerOptionsFromPayload(activeCustomersInfo),
    [activeCustomersInfo]
  );

  // useEffect(() => {
  //   setSelectAllAirlines(false);
  // }, [ruleInfo]);

  useEffect(() => {
    dispatch({ type: GET_AGENCY_BOOKING_REPORT_RULE_RESET });
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      navigate("/");
    } else {
      if (id === "new") {
        dispatch(getActiveCustomers());
      }
    }
  }, [dispatch, id, navigate, token]);

  useEffect(() => {
    if (id === "new") {
      setRule_name("");
      setIsActive(false);
      setPCCs("");
      setCustomerId();
      setSelectedCustomerOption(null);
      dispatch({ type: GET_AGENCY_BOOKING_REPORT_RULE_RESET });
    } else {
      dispatch(getAgencyBookingReportRuleByID(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (ruleInfo && Object.keys(ruleInfo).length > 0) {
      setRule_name(ruleInfo.agency_name || ruleInfo.rule_name || "");
      setIsActive(ruleInfo.active || false);
      setPCCs(ruleInfo.pccs || "");
      setCustomerId(ruleInfo.customer_id);
    }
  }, [ruleInfo]);

  useEffect(() => {
    if (!customerOptions.length) return;

    if (!customerId && !rule_name) {
      setSelectedCustomerOption(null);
      return;
    }

    const matchedCustomer = customerOptions.find(
      (customer) =>
        customer?.customer_id === customerId ||
        customer?.customer_name?.trim() === rule_name?.trim()
    );

    setSelectedCustomerOption(matchedCustomer || null);
  }, [customerId, customerOptions, rule_name]);

  useEffect(() => {
    if (customerById) {
      setPCCs(customerById.pccs);
      setCustomerId(customerById.customer_id);
    }
  }, [customerById]);
  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
    setErrorAlert(false);
  };

  const handleIsActive = (event) => {
    setIsActive(event.target.checked);
  };

  const handlePCCs = (event) => {
    const enteredValues = event.target.value
      .split("\n")
      .map((value) => value?.trim());
    const pccs = enteredValues.join(" ");
    setPCCs(pccs);
  };

  const submitHandler = () => {
    if (rule_name && PCCs && customerId) {
      const agency_booking_report_rule = {
        rule_name: rule_name?.trim(),
        active: isActive || false,
        pccs: PCCs?.trim(),
        airlines: DEFAULT_AIRLINES,
        success_queue: DEFAULT_SUCCESS_QUEUE,
        run_time: DEFAULT_RUN_TIME,
        customer_id: customerId,
        pick_from_category: DEFAULT_PICK_FROM_CATEGORY,
        ticketed_status: DEFAULT_TICKETED_STATUS,
        is_email_enabled: DEFAULT_EMAIL_ENABLED,
      };
      if (id === "new") {
        dispatch(createAgencyBookingReportRule(agency_booking_report_rule));
        setSnackOpen(true);

        // Reset all states
        setRule_name("");
        setIsActive(false);
        setPCCs("");
        setCustomerId();

        setTimeout(() => {
          navigate("/agencyBookingReportRules");
        }, 3000);
      } else {
        dispatch(
          updateAgencyBookingReportRule(id, agency_booking_report_rule)
        );
        setSnackOpen(true);

        setTimeout(() => {
          navigate("/agencyBookingReportRules");
        }, 3000);
      }
    } else {
      setSnackOpen(true);
      setErrorAlert(true);
    }
  };

  return (
    <Container sx={{ marginBottom: "5%" }}>
      <Box>
        <Box sx={{ margin: 3, fontSize: 25 }}>
          Manage Agency Booking Report Rules
          <Divider sx={{ marginTop: 2 }}></Divider>
        </Box>
        <Box sx={{ fontSize: 25, margin: 3 }}>
          {id === "new"
            ? "Add Agency Booking Report Rule"
            : "Edit Agency Booking Report Rule"}
        </Box>
        <Box sx={{ margin: 3 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid item xs={6}>
                Agency Name
              </Grid>
              <Grid item xs={6} mt={1}>
                {id === "new" ? (
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    loading={customerOptionsLoading}
                    loadingText="Loading agencies..."
                    isOptionEqualToValue={(option, value) =>
                      option?.customer_id === value?.customer_id
                    }
                    filterOptions={(options, { inputValue }) => {
                      const searchValue = inputValue.trim().toLowerCase();

                      if (!searchValue) {
                        return options.slice(0, 100);
                      }

                      return options
                        .filter((option) =>
                          option?.customer_name
                            ?.trim()
                            .toLowerCase()
                            .includes(searchValue)
                        )
                        .slice(0, 100);
                    }}
                    onChange={(e, newValue) => {
                      setSelectedCustomerOption(newValue || null);
                      if (newValue) {
                        setRule_name(newValue.customer_name);
                        setCustomerId(newValue.customer_id);
                        dispatch(getCustomerByID(newValue.customer_id));
                      } else {
                        setRule_name("");
                        setCustomerId();
                      }
                    }}
                    getOptionLabel={(option) =>
                      option?.customer_name?.trim() || ""
                    }
                    value={selectedCustomerOption}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={
                            customerOptionsLoading
                              ? "Loading agencies..."
                              : "Select agency"
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {customerOptionsLoading ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      );
                    }}
                  />
                ) : (
                  <TextField fullWidth disabled value={rule_name} />
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} mt={1}>
              Active?
              <Checkbox checked={isActive} onChange={handleIsActive} />
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={6} mt={2}>
                PCCs
              </Grid>
              <Grid item xs={6} mt={1}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  disabled
                  onChange={handlePCCs}
                  value={PCCs ? PCCs.split(" ").join(" ") : ""}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Grid item xs={6}>
                Run time
              </Grid>
              <Grid item xs={6} mt={1}>
                <TextField
                  fullWidth
                  disabled
                  value="02:00 AM"
                  helperText="This is currently fixed for agency booking reports."
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Button
                onClick={submitHandler}
                variant="contained"
                type="submit"
                sx={{ backgroundColor: "#282828" }}
              >
                Save
              </Button>
            </Grid>
          </Grid>
          {agencyBookingReportRuleInfo && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Success</AlertTitle>
                {agencyBookingReportRuleInfo}
              </Alert>
            </Snackbar>
          )}
          {error && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            </Snackbar>
          )}
          {success && message && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Success</AlertTitle>
                {message}
              </Alert>
            </Snackbar>
          )}
          {updateerror && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Error</AlertTitle>
                {updateerror}
              </Alert>
            </Snackbar>
          )}
          {errorAlert && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Required field error</AlertTitle>
                Please enter all the fields.
              </Alert>
            </Snackbar>
          )}
        </Box>
      </Box>
    </Container>
  );
}
